import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/orderService';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

// Add Razorpay script to document head
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutWrapper = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xxl} 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.background.secondary} 0%, ${props => props.theme.colors.background.primary} 100%);
  padding-top: 90px; /* Account for fixed header */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const CheckoutHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};
  text-align: center;
`;

const CheckoutTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CheckoutSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CartTypeIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background: ${props => {
    if (props.type === 'virtual') return props.theme.colors.info + '15';
    if (props.type === 'free') return props.theme.colors.success + '15';
    return props.theme.colors.primary + '15';
  }};
  border: 1px solid ${props => {
    if (props.type === 'virtual') return props.theme.colors.info + '30';
    if (props.type === 'free') return props.theme.colors.success + '30';
    return props.theme.colors.primary + '30';
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  color: ${props => {
    if (props.type === 'virtual') return props.theme.colors.info;
    if (props.type === 'free') return props.theme.colors.success;
    return props.theme.colors.primary;
  }};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CheckoutContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const CheckoutForm = styled.form`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.colors.border.light};
  height: fit-content;
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${props => props.theme.fontSize.lg};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  grid-column: 1 / -1;
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.info}08;
  border: 1px solid ${props => props.theme.colors.info}20;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.info};
  font-size: ${props => props.theme.fontSize.sm};
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
`;

const PaymentMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const PaymentMethod = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.checked ? props.theme.colors.primary : props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};
  background: ${props => props.checked ? props.theme.colors.primary + '08' : 'white'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const PaymentMethodInput = styled.input`
  margin: 0;
  margin-top: 2px;
  accent-color: ${props => props.theme.colors.primary};
  transform: scale(1.2);
`;

const PaymentMethodInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  flex: 1;
`;

const PaymentMethodTitle = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const PaymentMethodDescription = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.4;
`;

const PaymentMethodIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.bold};
  margin-left: auto;
`;

const PaymentGatewayLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text.secondary};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.colors.border.light};
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.xl} ${props => props.theme.borderRadius.xl} 0 0;
`;

const SummaryTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const SummaryContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const OrderItems = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.background.secondary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const ItemName = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.3;
`;

const ItemDetails = styled.span`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const ItemPrice = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};

  &:last-child {
    border-bottom: none;
    margin-top: ${props => props.theme.spacing.lg};
    padding-top: ${props => props.theme.spacing.lg};
    border-top: 2px solid ${props => props.theme.colors.border.light};
  }
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.semibold};
`;

const TotalLabel = styled(SummaryLabel)`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const TotalValue = styled(SummaryValue)`
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.success}08;
  border: 1px solid ${props => props.theme.colors.success}20;
  color: ${props => props.theme.colors.success};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
`;

const CheckoutPage = () => {
  const { items, total, itemCount, clearCart, isVirtualCart, isFreeOrder, getCartType } = useCart();
  const { user } = useAuth(); // Don't require authentication
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // Get cart characteristics
  const isVirtual = isVirtualCart();
  const isFree = isFreeOrder();
  const cartType = getCartType();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
      email: user?.email || '',
      phone: user?.phone || '',
      address1: user?.address1 || '',
      address2: user?.address2 || '',
      city: user?.city || '',
      state: user?.state || '',
      postcode: user?.postcode || '',
      country: user?.country || 'IN',
    },
  });

  // Load Razorpay script on component mount
  useEffect(() => {
    if (!isFree) {
      const loadRazorpayScript = async () => {
        return new Promise((resolve) => {
          if (window.Razorpay) {
            resolve(true);
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => resolve(true);
          script.onerror = () => resolve(false);
          document.body.appendChild(script);
        });
      };
      loadRazorpayScript().then(setRazorpayLoaded);
    } else {
      setRazorpayLoaded(true);
    }
  }, [isFree]);

  // Fetch payment gateways on component mount
  useEffect(() => {
    const fetchPaymentGateways = async () => {
      try {
        setGatewaysLoading(true);
        
        if (isFree) {
          setPaymentMethod('free');
          setPaymentGateways([]);
          return;
        }

        const gateways = await orderService.getPaymentGateways();
        setPaymentGateways(gateways);
        
        const razorpayGateway = gateways.find(g => g.id === 'razorpay');
        if (razorpayGateway) {
          setPaymentMethod('razorpay');
        } else if (gateways.length > 0) {
          const preferredGateway = gateways.find(g => ['razorpay', 'stripe', 'paypal'].includes(g.id));
          setPaymentMethod(preferredGateway ? preferredGateway.id : gateways[0].id);
        }
      } catch (err) {
        console.error('Error fetching payment gateways:', err);
        setError('Failed to load payment methods');
      } finally {
        setGatewaysLoading(false);
      }
    };

    fetchPaymentGateways();
  }, [isFree]);

  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  const getPaymentMethodIcon = (gatewayId) => {
    const icons = {
      'bacs': '🏦',
      'cod': '💰',
      'paypal': 'PP',
      'stripe': '💳',
      'razorpay': 'RP',
      'square': '⏹️',
      'free': '🎁',
    };
    return icons[gatewayId] || '💳';
  };

  const getCartTypeInfo = () => {
    if (isFree) {
      return {
        type: 'free',
        icon: '🎁',
        text: 'Free Order - No Payment Required'
      };
    }
    if (isVirtual) {
      return {
        type: 'virtual',
        icon: '📱',
        text: 'Digital Products - Instant Delivery'
      };
    }
    return {
      type: 'physical',
      icon: '📦',
      text: 'Physical Products - Shipping Required'
    };
  };

  // Handle free orders (no payment required)
  const handleFreeOrder = async (orderData, billingDetails) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess('Processing your free order...');

      const userData = {
        firstName: billingDetails.name.split(' ')[0] || billingDetails.name,
        lastName: billingDetails.name.split(' ').slice(1).join(' ') || '',
        billingDetails: billingDetails,
      };

      const order = await orderService.createOrder(
        { ...orderData, status: 'completed', set_paid: true },
        billingDetails.email,
        userData
      );
      
      setSuccess('Order completed successfully! Redirecting...');
      clearCart();
      navigate(`/order-confirmation/${order.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Razorpay payment
  const handleRazorpayPayment = async (orderData, billingDetails) => {
    try {
      if (!razorpayLoaded) {
        throw new Error('Razorpay is not loaded. Please try again.');
      }

      setIsLoading(true);
      setError(null);
      setSuccess('Creating order...');

      const userData = {
        firstName: billingDetails.name.split(' ')[0] || billingDetails.name,
        lastName: billingDetails.name.split(' ').slice(1).join(' ') || '',
        billingDetails: billingDetails,
      };

      const { order, razorpayDetails } = await orderService.createOrderWithRazorpay(
        orderData,
        billingDetails.email,
        userData
      );

      setSuccess('Opening payment gateway...');

      const razorpayOptions = {
        key: razorpayDetails.razorpay_key_id,
        amount: razorpayDetails.amount,
        currency: razorpayDetails.currency,
        name: razorpayDetails.checkout_options.name,
        description: razorpayDetails.checkout_options.description,
        image: razorpayDetails.checkout_options.image,
        order_id: razorpayDetails.razorpay_order_id,
        theme: razorpayDetails.checkout_options.theme,
        prefill: {
          name: razorpayDetails.customer.name,
          email: razorpayDetails.customer.email,
          contact: razorpayDetails.customer.contact,
        },
        handler: async function (response) {
          try {
            setSuccess('Verifying payment...');
            await orderService.verifyRazorpayPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.id,
            });

            setSuccess('Payment verified! Redirecting...');
            await orderService.waitForPaymentCompletion(order.id);
            clearCart();
            navigate(`/order-confirmation/${order.id}`);
          } catch (verificationError) {
            console.error('Payment verification failed:', verificationError);
            setError(`Payment verification failed: ${verificationError.message}`);
            setIsLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            setError('Payment was cancelled. Please try again.');
            setIsLoading(false);
            setSuccess(null);
          }
        },
        retry: { enabled: true, max_count: 3 },
        timeout: 300,
        remember_customer: false,
        readonly: { email: true, contact: true, name: true }
      };

      const razorpayInstance = new window.Razorpay(razorpayOptions);
      
      razorpayInstance.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        setError(`Payment failed: ${response.error.description || 'Unknown error'}`);
        setIsLoading(false);
        setSuccess(null);
      });

      razorpayInstance.open();

    } catch (err) {
      console.error('Razorpay payment error:', err);
      setError(err.message);
      setIsLoading(false);
      setSuccess(null);
    }
  };

  // Handle other payment methods
  const handleOtherPayments = async (orderData, billingDetails) => {
    try {
      setIsLoading(true);
      setError(null);

      const userData = {
        firstName: billingDetails.name.split(' ')[0] || billingDetails.name,
        lastName: billingDetails.name.split(' ').slice(1).join(' ') || '',
        billingDetails: billingDetails,
      };

      const order = await orderService.createOrder(
        orderData, 
        billingDetails.email,
        userData
      );
      
      clearCart();
      navigate(`/order-confirmation/${order.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    // For free orders, payment method is not required
    if (!isFree && !paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    const billingDetails = {
      name: data.name,
      email: data.email,
      phone: data.phone,
    };

    // Only add address fields for physical products
    if (!isVirtual) {
      billingDetails.address1 = data.address1;
      billingDetails.address2 = data.address2;
      billingDetails.city = data.city;
      billingDetails.state = data.state;
      billingDetails.postcode = data.postcode;
      billingDetails.country = data.country;
    }

    const orderData = orderService.prepareOrderData(
      items,
      billingDetails,
      null,
      isFree ? 'free' : paymentMethod
    );

    // Handle different order types
    if (isFree) {
      await handleFreeOrder(orderData, billingDetails);
    } else if (paymentMethod === 'razorpay') {
      await handleRazorpayPayment(orderData, billingDetails);
    } else {
      await handleOtherPayments(orderData, billingDetails);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const cartTypeInfo = getCartTypeInfo();

  return (
    <CheckoutWrapper>
      <Container>
        <CheckoutContent>
          <CheckoutForm onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <ErrorMessage>
                <span>⚠️</span>
                <span>{error}</span>
              </ErrorMessage>
            )}
            {success && (
              <SuccessMessage>
                <span>✅</span>
                <span>{success}</span>
              </SuccessMessage>
            )}

            <FormSection>
              <SectionTitle>
                <SectionIcon>👤</SectionIcon>
                {isVirtual ? 'Customer Information' : 'Billing Information'}
              </SectionTitle>

              <FormRow>
                <Input
                  label="Full Name *"
                  placeholder="Enter your full name"
                  error={errors.name?.message}
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 2,
                      message: 'Name must be at least 2 characters long',
                    },
                  })}
                />
              </FormRow>

              <FormRow>
                <Input
                  label="Email Address *"
                  type="email"
                  placeholder="Enter your email address"
                  error={errors.email?.message}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Please enter a valid email',
                    },
                  })}
                />
              </FormRow>

              <FormRow>
                <Input
                  label="Phone Number *"
                  type="tel"
                  placeholder="Enter your phone number"
                  error={errors.phone?.message}
                  {...register('phone', {
                    required: 'Phone number is required',
                    minLength: {
                      value: 10,
                      message: 'Phone number must be at least 10 digits',
                    },
                  })}
                />
              </FormRow>

              {/* Only show address fields for physical products */}
              {!isVirtual && (
                <>
                  <FormRow>
                    <Input
                      label="Address *"
                      placeholder="Enter your address"
                      error={errors.address1?.message}
                      {...register('address1', {
                        required: 'Address is required',
                      })}
                    />
                  </FormRow>

                  <FormRow>
                    <Input
                      label="Address Line 2 (Optional)"
                      placeholder="Apartment, suite, unit, etc."
                      {...register('address2')}
                    />
                  </FormRow>

                  <FormGrid>
                    <Input
                      label="City *"
                      placeholder="Enter your city"
                      error={errors.city?.message}
                      {...register('city', {
                        required: 'City is required',
                      })}
                    />
                    <Input
                      label="State/Province *"
                      placeholder="Enter your state"
                      error={errors.state?.message}
                      {...register('state', {
                        required: 'State is required',
                      })}
                    />
                  </FormGrid>

                  <FormGrid>
                    <Input
                      label="Postal Code *"
                      placeholder="Enter postal code"
                      error={errors.postcode?.message}
                      {...register('postcode', {
                        required: 'Postal code is required',
                      })}
                    />
                    <Input
                      label="Country *"
                      placeholder="Enter country"
                      error={errors.country?.message}
                      {...register('country', {
                        required: 'Country is required',
                      })}
                    />
                  </FormGrid>
                </>
              )}
            </FormSection>

            {/* Only show payment methods section if order is not free */}
            {!isFree && (
              <FormSection>
                <SectionTitle>
                  <SectionIcon>💳</SectionIcon>
                  Payment Method
                </SectionTitle>
                
                {gatewaysLoading ? (
                  <PaymentGatewayLoader>
                    <LoadingSpinner size="20px" showText={false} />
                    <span style={{ marginLeft: '0.5rem' }}>Loading payment methods...</span>
                  </PaymentGatewayLoader>
                ) : (
                  <PaymentMethods>
                    {paymentGateways.map((gateway) => (
                      <PaymentMethod 
                        key={gateway.id} 
                        checked={paymentMethod === gateway.id}
                      >
                        <PaymentMethodInput
                          type="radio"
                          name="paymentMethod"
                          value={gateway.id}
                          checked={paymentMethod === gateway.id}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <PaymentMethodInfo>
                          <PaymentMethodTitle>{gateway.title}</PaymentMethodTitle>
                          <PaymentMethodDescription>
                            {gateway.description || 'No description available'}
                          </PaymentMethodDescription>
                        </PaymentMethodInfo>
                        <PaymentMethodIcon>
                          {getPaymentMethodIcon(gateway.id)}
                        </PaymentMethodIcon>
                      </PaymentMethod>
                    ))}
                  </PaymentMethods>
                )}
                
                {paymentGateways.length === 0 && !gatewaysLoading && (
                  <ErrorMessage>
                    <span>⚠️</span>
                    <span>No payment methods available. Please contact support.</span>
                  </ErrorMessage>
                )}
              </FormSection>
            )}

            <Button
              type="submit"
              size="lg"
              fullWidth
              disabled={isLoading || (!isFree && !paymentMethod) || (!isFree && paymentMethod === 'razorpay' && !razorpayLoaded)}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="20px" showText={false} />
                  <span style={{ marginLeft: '0.5rem' }}>
                    {isFree ? 'Processing Order...' : (paymentMethod === 'razorpay' ? 'Processing...' : 'Processing Order...')}
                  </span>
                </>
              ) : (
                isFree ? 'Complete Free Order' : (paymentMethod === 'razorpay' ? 'Pay with Razorpay' : 'Place Order')
              )}
            </Button>
          </CheckoutForm>

          <OrderSummary>
            <SummaryHeader>
              <SummaryTitle>
                <span>📋</span>
                Order Summary
              </SummaryTitle>
            </SummaryHeader>
            
            <SummaryContent>
              <OrderItems>
                {items.map((item) => (
                  <OrderItem key={item.id}>
                    <ItemImage image={item.image} />
                    <ItemInfo>
                      <ItemName>{item.name}</ItemName>
                      <ItemDetails>
                        Qty: {item.quantity} • {item.virtual || item.downloadable ? 'Digital' : 'Physical'}
                      </ItemDetails>
                    </ItemInfo>
                    <ItemPrice>{formatPrice(item.price * item.quantity)}</ItemPrice>
                  </OrderItem>
                ))}
              </OrderItems>

              <SummaryRow>
                <SummaryLabel>Subtotal ({itemCount} items)</SummaryLabel>
                <SummaryValue>{formatPrice(total)}</SummaryValue>
              </SummaryRow>

              {!isVirtual && (
                <SummaryRow>
                  <SummaryLabel>Shipping</SummaryLabel>
                  <SummaryValue>Free</SummaryValue>
                </SummaryRow>
              )}

              <SummaryRow>
                <SummaryLabel>Tax</SummaryLabel>
                <SummaryValue>₹0.00</SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <TotalLabel>Total</TotalLabel>
                <TotalValue>
                  {formatPrice(total)}
                  {isFree && <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#22c55e' }}>(FREE)</span>}
                </TotalValue>
              </SummaryRow>
            </SummaryContent>
          </OrderSummary>
        </CheckoutContent>
      </Container>
    </CheckoutWrapper>
  );
};

export default CheckoutPage;